import React, { useState } from 'react';
import { changePassword, logError } from '../api';
import { getUserId } from '../services/AuthService';
import { validatePasswordsMatch, validatePasswordComplexity } from '../utils/PasswordUtils';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    let passwordError = validatePasswordsMatch(newPassword, confirmNewPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    passwordError = validatePasswordComplexity(newPassword);
    if (passwordError) {
      setErrorMessage(passwordError);
      return;
    }

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setErrorMessage('Por favor, rellena todos los campos.');
      return;
    }

    try {
      setLoading(true);
      await changePassword(getUserId(), {
        OldPassword: currentPassword,
        NewPassword: newPassword,
      });

      setSuccessMessage('Contraseña cambiada con éxito');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTouchedFields({ new: false, confirm: false });
    } catch (error) {
      logError(error);
      setErrorMessage('Error al cambiar la contraseña. Comprueba la contraseña actual.');
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordInput = ({ label, value, onChange, visible, toggle, name, onBlur, showError }) => (
    <div className="mb-3 position-relative">
      <label className="form-label">{label}</label>
      <div className="input-group">
        <input
          type={visible ? 'text' : 'password'}
          className="form-control"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete="new-password"
        />
        <span className="input-group-text" onClick={toggle} style={{ cursor: 'pointer' }}>
          {visible ? <FaEyeSlash /> : <FaEye />}
        </span>
      </div>
      {showError && name === 'new' && touchedFields.new && (
        <div className="form-text text-danger">{validatePasswordComplexity(value)}</div>
      )}
      {showError && name === 'confirm' && touchedFields.confirm && (
        <div className="form-text text-danger">{validatePasswordsMatch(newPassword, value)}</div>
      )}
    </div>
  );

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header text-center">
              <h2>Cambiar Contraseña</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange}>
                {renderPasswordInput({
                  label: 'Contraseña actual',
                  value: currentPassword,
                  onChange: (e) => setCurrentPassword(e.target.value),
                  visible: passwordVisibility.current,
                  toggle: () => toggleVisibility('current'),
                  name: 'current',
                  showError: false,
                })}

                <p className="mb-1">La nueva contraseña debe contener:</p>
                <ul>
                  <li>Al menos 8 caracteres</li>
                  <li>Una letra mayúscula</li>
                  <li>Una letra minúscula</li>
                  <li>Un número</li>
                </ul>

                {renderPasswordInput({
                  label: 'Nueva contraseña',
                  value: newPassword,
                  onChange: (e) => setNewPassword(e.target.value),
                  visible: passwordVisibility.new,
                  toggle: () => toggleVisibility('new'),
                  name: 'new',
                  onBlur: () => setTouchedFields((prev) => ({ ...prev, new: true })),
                  showError: true,
                })}

                {renderPasswordInput({
                  label: 'Confirmar nueva contraseña',
                  value: confirmNewPassword,
                  onChange: (e) => setConfirmNewPassword(e.target.value),
                  visible: passwordVisibility.confirm,
                  toggle: () => toggleVisibility('confirm'),
                  name: 'confirm',
                  onBlur: () => setTouchedFields((prev) => ({ ...prev, confirm: true })),
                  showError: true,
                })}

                {errorMessage && (
                  <div className="alert alert-danger" role="alert">
                    {errorMessage}
                  </div>
                )}
                {successMessage && (
                  <div className="alert alert-success" role="alert">
                    {successMessage}
                  </div>
                )}

                <div className="d-grid">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Cambiando...' : 'Cambiar Contraseña'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
